import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Clock } from 'lucide-react';
import sabotagelogo from '@/assets/sabotage-logo.png';
import { getPendingKyc, clearPendingKyc, recheckKycGate, type PendingKyc } from '@/lib/personaKyc';

// Web return target for the Persona hosted flow (redirect-uri = /persona-return).
// Persona appends inquiry-id / reference-id / status / fields - ADVISORY ONLY. The
// persona-webhook updates kyc_status server-side; we poll check_kyc_gate
// (recheckKycGate) so the webhook has landed before returning the user to where
// they launched. Native returns through NativeUrlHandler's persona-return branch.
//
// On SUCCESS we auto-return. On poll exhaustion (webhook delayed / not yet landed)
// we do NOT spin forever and we do NOT silently dump the user on a still-blocked
// page - we hold on an actionable "still processing" state with Check-again + Back,
// keeping pendingKyc (1h TTL) so a later landing still resolves.
export default function PersonaReturn() {
  const navigate = useNavigate();
  const started = useRef(false);
  const [pending, setPending] = useState<PendingKyc | null>(null);
  const [phase, setPhase] = useState<'checking' | 'processing'>('checking');

  const check = useCallback(async (pj: PendingKyc) => {
    setPhase('checking');
    const ok = await recheckKycGate(pj.userId, pj.tier);
    if (ok) {
      clearPendingKyc();
      navigate(pj.returnPath.startsWith('/') ? pj.returnPath : '/wallet', { replace: true });
    } else {
      setPhase('processing'); // webhook not landed yet - surface an actionable state
    }
  }, [navigate]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const pj = getPendingKyc();
    if (!pj) { navigate('/wallet', { replace: true }); return; }
    setPending(pj);
    void check(pj);
  }, [navigate, check]);

  const back = () => {
    // Return to where they launched WITHOUT clearing pendingKyc - if the webhook
    // lands later, that page re-reads the gate; and a fresh /persona-return would
    // still resolve within the TTL. No re-doing the inquiry.
    navigate(pending?.returnPath?.startsWith('/') ? pending.returnPath : '/wallet', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-border bg-card">
        <CardContent className="p-8 text-center space-y-4">
          <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-16 w-auto mx-auto" />
          {phase === 'checking' ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">Confirming your verification…</p>
            </>
          ) : (
            <>
              <Clock className="h-8 w-8 text-muted-foreground mx-auto" />
              <h1 className="text-lg font-display font-bold text-foreground">Almost there</h1>
              <p className="text-sm text-muted-foreground">
                We're still confirming your verification. This usually takes a moment and updates
                automatically, with no need to verify again.
              </p>
              <div className="space-y-2 pt-1">
                <Button
                  className="w-full bg-primary hover:bg-primary/90"
                  onClick={() => pending && check(pending)}
                >
                  Check again
                </Button>
                <Button variant="outline" className="w-full" onClick={back}>
                  Back to wallet
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
